// Claude | 2026-10-23 | P7: framework-free helpers for the supplier reliability figures shown to buyers.
// The API (POST /supplier/reliability, and `reliability` on each AI suggestion) decides the thresholds: a figure below
// the minimum sample, or from fewer than the minimum distinct buyers, arrives with value null, NO n and a reason. It is
// shown as "Fewer than 5 RFQs" / "Too few buyers yet" - never as 0% or 100%. With the API switch off
// (RELIABILITY_DISPLAY, off on prod by default) no object arrives and nothing is rendered.

export type ReliabilityReason = 'FEW_SAMPLES' | 'FEW_BUYERS' | null;
export interface ReliabilityRate { value: number | null; n: number | null; reason?: ReliabilityReason; }
export interface SupplierReliability {
  supplierId: number;
  minSample: number;
  minBuyers?: number;
  windowDays: number;
  responseRate: ReliabilityRate;
  responseTime: { medianHours: number | null; n: number | null; reason?: ReliabilityReason };
  onTimeDelivery: ReliabilityRate;
  documents: { kyc: 'APPROVED' | 'PENDING' | 'NOT_VERIFIED'; certificates: { withExpiry: number; valid: number; withoutExpiry: number } };
}

export interface ReliabilityLine { key: string; label: string; value: string; detail: string; tone: 'good' | 'neutral' | 'none'; tooltip: string; }

export const TOO_FEW_BUYERS = 'Too few buyers yet';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "under 1 h", "~5 h", "~2 d" (hours below a day stay in hours: a same-day and a 3-day responder must look different). */
export function formatHours(h: number | null | undefined): string {
  if (h === null || h === undefined || !Number.isFinite(h)) return '';
  if (h < 1) return 'under 1 h';
  if (h < 24) return `~${Math.round(h)} h`;
  return `~${Math.round(h / 24)} d`;
}

/** What to show when a figure is withheld (pure). */
function withheld(reason: ReliabilityReason | undefined, min: number, many: string): string {
  return reason === 'FEW_BUYERS' ? TOO_FEW_BUYERS : `Fewer than ${min} ${many}`;
}

/** The lines shown for one supplier, in display order (pure; unit-tested). */
export function reliabilityLines(r: SupplierReliability | null | undefined): ReliabilityLine[] {
  if (!r) return [];
  const min = r.minSample || 5;
  const minB = r.minBuyers || 2;
  const lines: ReliabilityLine[] = [];
  const rr = r.responseRate || { value: null, n: null };
  lines.push({
    key: 'response', label: 'Quotes',
    value: rr.value === null ? withheld(rr.reason, min, 'RFQs') : `${rr.value}%`,
    detail: rr.value === null || rr.n === null ? '' : `of ${plural(rr.n, 'RFQ', 'RFQs')}`,
    tone: rr.value === null ? 'none' : 'neutral',
    tooltip: `Share of RFQs this supplier answered with a submitted quotation (last ${r.windowDays} days). Shown from ${min} RFQs from at least ${minB} buyers.`,
  });
  const rt = r.responseTime || { medianHours: null, n: null };
  lines.push({
    key: 'time', label: 'Typical reply',
    value: rt.medianHours === null ? withheld(rt.reason, min, 'quotes') : formatHours(rt.medianHours),
    detail: rt.medianHours === null || rt.n === null ? '' : `median of ${plural(rt.n, 'quote', 'quotes')}`,
    tone: rt.medianHours === null ? 'none' : 'neutral',
    tooltip: `Median time from the RFQ being sent to the quotation being submitted. Shown from ${min} quotes from at least ${minB} buyers.`,
  });
  const ot = r.onTimeDelivery || { value: null, n: null };
  lines.push({
    key: 'delivery', label: 'On time',
    value: ot.value === null ? withheld(ot.reason, min, 'deliveries') : `${ot.value}%`,
    detail: ot.value === null || ot.n === null ? '' : `of ${plural(ot.n, 'delivery', 'deliveries')}`,
    tone: ot.value === null ? 'none' : 'neutral',
    tooltip: `Orders received on or before the delivery date the supplier gave when accepting the order, out of orders with a confirmed receipt. Shown from ${min} deliveries from at least ${minB} buyers.`,
  });
  const d = r.documents || { kyc: 'NOT_VERIFIED', certificates: { withExpiry: 0, valid: 0, withoutExpiry: 0 } };
  const kycText = d.kyc === 'APPROVED' ? 'KYC verified' : d.kyc === 'PENDING' ? 'KYC under review' : 'KYC not verified';
  const c = d.certificates || { withExpiry: 0, valid: 0, withoutExpiry: 0 };
  lines.push({
    key: 'documents', label: 'Documents', value: kycText,
    detail: c.withExpiry > 0 ? `${c.valid} of ${plural(c.withExpiry, 'dated certificate', 'dated certificates')} in date` : 'no dated certificates',
    tone: d.kyc === 'APPROVED' ? 'good' : 'none',
    tooltip: 'KYC = the supplier\'s latest KYC application. "In date" = the certificate\'s expiry date has not passed; it is not a check of the certificate itself.',
  });
  return lines;
}
